/**
 * Minimal auth hook — reads Google identity from sessionStorage.
 * Exposes current user info and a signOut action.
 *
 * UserRole: 1 = EMPLOYEE, 2 = MANAGER, 3 = ADMIN, 4 = PARTNERS_MNG,
 *           5 = CATALOG_MNG, 6 = ORDERS_MNG
 */

export type UserRole = 1 | 2 | 3 | 4 | 5 | 6;

export interface AuthUser {
  displayName: string;
  email: string;
  role: UserRole;
  roles?: UserRole[];
  capabilities?: string[];
  userId: string;
}

function parseUser(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem('cre8or_auth_user');
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function useAuth() {
  const user = parseUser();

  function signOut() {
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('cre8or_auth_user');
    window.location.href = '/login';
  }

  return { user, signOut };
}

/**
 * Returns true if the current user has at least one of the specified roles.
 */
export function useRequireRole(allowedRoles: UserRole[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  const effectiveRoles = user.roles && user.roles.length > 0
    ? user.roles
    : [user.role];
  return effectiveRoles.some((role) => allowedRoles.includes(role));
}

export function useHasCapability(capability: string): boolean {
  const { user } = useAuth();
  if (!user?.capabilities) return false;
  return user.capabilities.includes(capability);
}
